import { useEffect, useState } from "react";
import useReveal from "@/hooks/useReveal";

interface EmergencyNearbyMapProps {
  districtSlug: string;
  districtLabel: string;
}

interface Coords {
  lat: number;
  lng: number;
}

type GeoStatus = "idle" | "loading" | "active" | "error";

const legendItems = [
  { id: "map-legend-1", icon: "ri-hospital-line", label: "Multispeciality hospitals" },
  { id: "map-legend-2", icon: "ri-heart-pulse-line", label: "Trauma & cardiac care" },
  { id: "map-legend-3", icon: "ri-first-aid-kit-line", label: "Blood banks & pharmacies" },
];

export default function EmergencyNearbyMap({
  districtSlug,
  districtLabel,
}: EmergencyNearbyMapProps) {
  const reveal = useReveal<HTMLDivElement>(0.1);
  const [coords, setCoords] = useState<Coords | null>(null);
  const [geoStatus, setGeoStatus] = useState<GeoStatus>("idle");
  const [geoMessage, setGeoMessage] = useState("");

  // A district chip always wins — clear any personal-location override.
  useEffect(() => {
    setCoords(null);
    setGeoStatus("idle");
    setGeoMessage("");
  }, [districtSlug]);

  const handleLocate = () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoStatus("error");
      setGeoMessage("Your browser can't share your location — pick a district above instead.");
      return;
    }
    setGeoStatus("loading");
    setGeoMessage("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
        setGeoStatus("active");
        setGeoMessage("");
      },
      () => {
        setGeoStatus("error");
        setGeoMessage(
          "We couldn't access your location. Choose a district above to see nearby hospitals.",
        );
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  };

  const handleReset = () => {
    setCoords(null);
    setGeoStatus("idle");
    setGeoMessage("");
  };

  const locatedCoords = geoStatus === "active" ? coords : null;
  const scope = locatedCoords
    ? "your location"
    : districtSlug
      ? `${districtLabel}, Karnataka, India`
      : "Coastal Karnataka, India";
  const query = locatedCoords
    ? `hospitals near ${locatedCoords.lat},${locatedCoords.lng}`
    : `hospitals in ${scope}`;
  const zoom = locatedCoords ? 13 : districtSlug ? 11 : 9;
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=${zoom}&output=embed`;
  const searchHref = `https://www.google.com/maps/search/${encodeURIComponent(query)}`;
  const mapKey = locatedCoords
    ? `${locatedCoords.lat},${locatedCoords.lng}`
    : districtSlug || "all";

  const heading = locatedCoords
    ? "Emergency care near you"
    : `Emergency care around ${districtLabel}`;
  const blurb = locatedCoords
    ? "Showing hospitals around your current location. Pick a district above to switch back to a broader view."
    : "This mini-map follows the area you pick above, so you can spot the closest hospitals and clinics before you set off.";

  return (
    <div
      ref={reveal.ref}
      className={`grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:grid-cols-[320px_minmax(0,1fr)] ${
        reveal.visible ? "animate-fade-up" : "opacity-0"
      }`}
    >
      <div className="flex flex-col justify-between gap-5 border-b border-background-200 bg-background-100 p-5 lg:border-b-0 lg:border-r">
        <div>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-secondary-900">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={`${locatedCoords ? "ri-crosshair-2-line" : "ri-map-pin-line"} text-sm`} />
            </span>
            {locatedCoords ? "Around you" : "Nearby hospitals"}
          </span>
          <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
            {heading}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">{blurb}</p>
        </div>

        <div className="flex flex-col gap-2">
          {legendItems.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-2.5 text-xs font-medium text-foreground-700"
            >
              <span className="flex h-6 w-6 flex-none items-center justify-center rounded-md bg-accent-100 text-accent-800">
                <i className={`${item.icon} text-sm`} />
              </span>
              {item.label}
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          {locatedCoords ? (
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-map-2-line text-base" />
              </span>
              Back to district view
            </button>
          ) : (
            <button
              type="button"
              onClick={handleLocate}
              disabled={geoStatus === "loading"}
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-wait disabled:opacity-70"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i
                  className={`${
                    geoStatus === "loading" ? "ri-loader-4-line animate-spin" : "ri-crosshair-2-line"
                  } text-base`}
                />
              </span>
              {geoStatus === "loading" ? "Finding you…" : "Use my location"}
            </button>
          )}

          <a
            href={searchHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-external-link-line text-base" />
            </span>
            Open full map
          </a>
        </div>

        {geoMessage ? (
          <p className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50/70 px-3 py-2 text-xs leading-relaxed text-red-700">
            <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center">
              <i className="ri-error-warning-line text-sm" />
            </span>
            {geoMessage}
          </p>
        ) : null}
      </div>

      <div className="relative h-[240px] w-full bg-background-200 md:h-[320px] lg:h-auto lg:min-h-[340px]">
        <iframe
          key={mapKey}
          title={`Map of hospitals near ${scope}`}
          src={mapSrc}
          className="h-full w-full border-0"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </div>
  );
}